import { FaCalendarAlt, FaPlus, FaTrash } from 'react-icons/fa'

import Button from 'components/button'

import DayCityEditor from '../../../Update/DayCityEditor'
import * as U from '../../../Update/style'
import * as S from '../../style'

const emptyCityData = () => ({
  id: '',
  name: '',
  description: '',
  latitude: 0,
  longitude: 0,
  rate: null,
  accommodations: [],
  images: []
})

const emptyDay = (dayNumber: number) => ({
  dayNumber,
  date: null,
  title: '',
  cities: [emptyCityData()]
})

const addDaysToDate = (dateValue: string | null, offset: number) => {
  if (!dateValue) return ''

  const [year, month, day] = dateValue.split('-').map(Number)
  const date = new Date(year, month - 1, day + offset)

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')
}

type PainelCitiesProps = {
  handleChange: any
  handleBlur: any
  values: any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  className: string
  nextStep: () => void
  prevStep: () => void
}

const PainelCities = ({
  values,
  setFieldValue,
  className,
  nextStep,
  prevStep
}: PainelCitiesProps) => {
  const dayPlan = Array.isArray(values.dayPlan) ? values.dayPlan : []
  const made = values.made === true || String(values.made) === 'true'

  const normalizeDays = (days: any[]) =>
    days.map((day, index) => ({
      ...day,
      dayNumber: index + 1,
      date: made ? addDaysToDate(values.tripDate, index) : null,
      cities:
        Array.isArray(day?.cities) && day.cities.length > 0
          ? day.cities
          : [emptyCityData()]
    }))

  const updateDays = (days: any[]) => {
    const normalized = normalizeDays(days)
    setFieldValue('dayPlan', normalized)
    setFieldValue('days', normalized.length)
  }

  const addDay = () => {
    updateDays([...dayPlan, emptyDay(dayPlan.length + 1)])
  }

  const removeDay = (dayIndex: number) => {
    if (dayPlan.length <= 1) return
    updateDays(dayPlan.filter((_: any, index: number) => index !== dayIndex))
  }

  const addCity = (dayIndex: number) => {
    const next = dayPlan.map((day: any, index: number) =>
      index === dayIndex
        ? {
            ...day,
            cities: [...(day.cities || []), emptyCityData()]
          }
        : day
    )
    updateDays(next)
  }

  const removeCity = (dayIndex: number, cityIndex: number) => {
    const currentCities = dayPlan?.[dayIndex]?.cities || []
    if (currentCities.length <= 1) return

    const next = dayPlan.map((day: any, index: number) =>
      index === dayIndex
        ? {
            ...day,
            cities: currentCities.filter(
              (_: any, index: number) => index !== cityIndex
            )
          }
        : day
    )
    updateDays(next)
  }

  return (
    <S.PainelCreateItinerary className={className}>
      <S.CreateDaysWorkspace>
        <U.SectionHeader>
          <div>
            <h2>Dia a dia da viagem</h2>
            <p>
              Organize cada dia da viagem. Adicione cidades, fotos e suas
              anotações exatamente como na edição do roteiro.
            </p>
          </div>

          <U.AddDayButton type="button" onClick={addDay}>
            <FaPlus />
            Adicionar novo dia
          </U.AddDayButton>
        </U.SectionHeader>

        <U.DaysList>
          {dayPlan.map((day: any, dayIndex: number) => {
            const cities = Array.isArray(day.cities) ? day.cities : []
            const firstCityName =
              cities.find((city: any) => String(city?.name || '').trim())
                ?.name || 'Adicione uma cidade'

            return (
              <U.DayCard key={day.id || dayIndex}>
                <U.DayHeader>
                  <U.DayNumber>
                    Dia {dayIndex + 1}
                    <span>{firstCityName}</span>
                  </U.DayNumber>

                  <U.DayDate>
                    <FaCalendarAlt />
                    <input
                      type="date"
                      value={
                        made
                          ? addDaysToDate(values.tripDate, dayIndex)
                          : day?.date || ''
                      }
                      disabled={made}
                      onChange={(event) =>
                        setFieldValue(
                          `dayPlan.${dayIndex}.date`,
                          event.target.value || null
                        )
                      }
                      aria-label={`Data do Dia ${dayIndex + 1}`}
                    />
                  </U.DayDate>

                  <U.DayTitleInput
                    type="text"
                    value={day?.title || ''}
                    placeholder="Título do dia (opcional)"
                    onChange={(event) =>
                      setFieldValue(
                        `dayPlan.${dayIndex}.title`,
                        event.target.value
                      )
                    }
                  />

                  <U.DeleteDayButton
                    type="button"
                    disabled={dayPlan.length <= 1}
                    aria-label={`Remover Dia ${dayIndex + 1}`}
                    title={
                      dayPlan.length <= 1
                        ? 'O roteiro precisa ter ao menos um dia'
                        : 'Remover dia'
                    }
                    onClick={() => removeDay(dayIndex)}
                  >
                    <FaTrash />
                  </U.DeleteDayButton>
                </U.DayHeader>

                <U.DayCities>
                  {cities.map((city: any, cityIndex: number) => (
                    <DayCityEditor
                      key={city.id || `${dayIndex}-${cityIndex}`}
                      dayIndex={dayIndex}
                      cityIndex={cityIndex}
                      city={city}
                      setFieldValue={setFieldValue}
                      removeCity={() => removeCity(dayIndex, cityIndex)}
                    />
                  ))}

                  <U.AddCityButton
                    type="button"
                    onClick={() => addCity(dayIndex)}
                  >
                    <FaPlus />
                    Adicionar cidade ao dia
                  </U.AddCityButton>
                </U.DayCities>
              </U.DayCard>
            )
          })}
        </U.DaysList>
      </S.CreateDaysWorkspace>

      <S.ContentCreateItinerary>
        <Button type="button" onClick={nextStep}>
          <p>Continuar</p>
        </Button>
        <Button type="button" secondary onClick={prevStep}>
          <p>Voltar</p>
        </Button>
      </S.ContentCreateItinerary>
    </S.PainelCreateItinerary>
  )
}

export default PainelCities
